import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { PiggyBank, Save, Wallet } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ASSET_CLASS_BY_ID } from '@/constants/asset-classes';
import { Layout } from '@/components/Layout';
import { useI18n } from '@/i18n/use-i18n';
import { getIntlLocale } from '@/i18n/locale';
import {
  assetClassesService,
  contributionsService,
  targetAssetsService,
  type AssetClassRecord,
  type ContributionRecommendationRecord,
  type ContributionsResult,
  type TargetAssetRecord,
} from '@/services';
import './contributions.css';

type Recommendation = ContributionRecommendationRecord & {
  className: string;
  classColor: string;
  price: number;
  quantity: number;
  suggestedValue: number;
};

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
};

export function Contributions() {
  const { locale, strings } = useI18n();
  const intlLocale = getIntlLocale(locale);
  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        style: 'currency',
        currency: 'BRL',
        maximumFractionDigits: 2,
      }),
    [intlLocale],
  );
  const currencyInputFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [intlLocale],
  );
  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(intlLocale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [intlLocale],
  );
  const assetClassCopy = strings.assetClassesCatalog;
  const [amount, setAmount] = useState(0);
  const [amountInput, setAmountInput] = useState(() => currencyInputFormatter.format(0));
  const [amountTouched, setAmountTouched] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const debouncedAmount = useDebouncedValue(amount, 450);
  const navigate = useNavigate();

  const {
    data: assetClasses,
    isLoading,
    isError,
    error,
    isFetching,
  } = useQuery<AssetClassRecord[]>({
    queryKey: ['asset-classes'],
    queryFn: assetClassesService.list,
  });

  const {
    data: targetAssets,
    isLoading: isLoadingAssets,
    isError: isAssetsError,
    error: assetsError,
    isFetching: isFetchingAssets,
  } = useQuery<TargetAssetRecord[]>({
    queryKey: ['target-assets'],
    queryFn: targetAssetsService.list,
  });

  const {
    data: contributions,
    isLoading: isLoadingContributions,
    isError: isContributionsError,
    error: contributionsError,
    isFetching: isFetchingContributions,
  } = useQuery<ContributionsResult>({
    queryKey: ['contributions', debouncedAmount],
    queryFn: () => contributionsService.simulate(debouncedAmount),
    enabled: debouncedAmount > 0,
    placeholderData: keepPreviousData,
  });

  const saveContributionMutation = useMutation({
    mutationFn: (payload: { amount: number; result: ContributionsResult }) =>
      contributionsService.save(payload),
    onSuccess: (saved) => {
      setSaveFeedback({ type: 'success', message: strings.contributions.saveSuccess });
      consolidatePortfolioMutation.mutate(saved.id);
    },
    onError: (mutationError) => {
      setSaveFeedback({
        type: 'error',
        message: mutationError instanceof Error ? mutationError.message : strings.contributions.errors.save,
      });
    },
  });

  const consolidatePortfolioMutation = useMutation({
    mutationFn: (contributionId: string) =>
      contributionsService.consolidatePortfolio(contributionId),
    onSuccess: () => {
      navigate('/dashboard');
    },
    onError: (mutationError) => {
      setSaveFeedback({
        type: 'error',
        message:
          mutationError instanceof Error
            ? mutationError.message
            : strings.contributions.errors.consolidate,
      });
    },
  });

  const hasAmount = amount > 0;
  const hasAssetClassesData = assetClasses !== undefined;
  const hasTargetAssetsData = targetAssets !== undefined;

  const isLoadingStaticData =
    (!hasAssetClassesData && (isLoading || isFetching)) ||
    (!hasTargetAssetsData && (isLoadingAssets || isFetchingAssets));

  const isLoadingContributionsData =
    hasAmount && !contributions && (isLoadingContributions || isFetchingContributions);

  const isLoadingData = isLoadingStaticData || isLoadingContributionsData;
  const amountIsInvalid = amount <= 0;
  const showAmountError = amountTouched && amountIsInvalid;

  const recommendations = useMemo<Recommendation[]>(() => {
    if (!hasAmount) return [];
    const items = contributions?.recommendations ?? [];

    return items
      .map((item) => {
        const assetClassCode = typeof item.assetClassCode === 'string' ? item.assetClassCode : undefined;
        const classDefinition =
          assetClassCode && assetClassCode in ASSET_CLASS_BY_ID
            ? ASSET_CLASS_BY_ID[assetClassCode as keyof typeof ASSET_CLASS_BY_ID]
            : undefined;
        const classCopy =
          assetClassCode && assetClassCode in assetClassCopy
            ? assetClassCopy[assetClassCode as keyof typeof assetClassCopy]
            : undefined;
        const className =
          item.assetClassName ??
          classCopy?.name ??
          classDefinition?.name ??
          strings.contributions.classFallback;
        const classColor = item.assetClassColor ?? classDefinition?.color ?? 'var(--color-brand)';
        const price = Number.isFinite(item.price) ? item.price : 0;
        const quantity = Number.isFinite(item.quantity) ? item.quantity : 0;
        const suggestedValue = Number.isFinite(item.suggestedValue)
          ? item.suggestedValue
          : price > 0
            ? price * quantity
            : 0;

        return {
          ...item,
          ticker: item.ticker.toUpperCase(),
          className,
          classColor,
          price,
          quantity,
          suggestedValue,
        };
      })
      .sort((a, b) => a.className.localeCompare(b.className) || b.rating - a.rating);
  }, [assetClassCopy, contributions, hasAmount, strings.contributions.classFallback]);

  const totalSuggested = useMemo(
    () => (hasAmount ? contributions?.totalDistributed ?? amount : 0),
    [amount, contributions?.totalDistributed, hasAmount],
  );

  const totalCashRemainder = useMemo(
    () => (hasAmount ? Math.max(0, contributions?.totalCashRemainder ?? 0) : 0),
    [contributions?.totalCashRemainder, hasAmount],
  );
  const hasRemainder = totalCashRemainder > 0;

  const totalInvested = useMemo(
    () => (hasAmount ? Math.max(0, totalSuggested - totalCashRemainder) : 0),
    [hasAmount, totalCashRemainder, totalSuggested],
  );

  const assetClassesCount = contributions?.assetClassesCount ?? assetClasses?.length ?? 0;
  const eligibleAssetsCount = contributions?.eligibleAssetsCount ?? targetAssets?.length ?? 0;
  const hasAssetClasses = assetClassesCount > 0;
  const hasEligibleAssets = eligibleAssetsCount > 0;
  const isLoadingRecommendations = isLoadingContributionsData;
  const totalSuggestedValue = recommendations.reduce((sum, item) => sum + item.suggestedValue, 0);

  const queryErrorMessage = isError
    ? error instanceof Error
      ? error.message
      : strings.contributions.errors.assetClasses
    : '';
  const assetsErrorMessage = isAssetsError
    ? assetsError instanceof Error
      ? assetsError.message
      : strings.contributions.errors.assets
    : '';
  const contributionsErrorMessage = isContributionsError
    ? contributionsError instanceof Error
      ? contributionsError.message
      : strings.contributions.errors.contributions
    : '';
  const combinedErrorMessage = contributionsErrorMessage || queryErrorMessage || assetsErrorMessage;
  const hasRecommendations = recommendations.length > 0;
  const canSaveContribution =
    hasAmount && !!contributions && hasRecommendations && !combinedErrorMessage && !isLoadingData;
  const isSavingContribution =
    saveContributionMutation.isPending || consolidatePortfolioMutation.isPending;
  const saveButtonLabel = consolidatePortfolioMutation.isPending
    ? strings.contributions.saveButton.consolidating
    : isSavingContribution
      ? strings.contributions.saveButton.saving
      : strings.contributions.saveButton.save;
  const saveDisabledReason = !canSaveContribution
    ? !hasAmount
      ? ''
      : !hasAssetClasses
        ? strings.contributions.disabledReason.noClasses
        : !hasEligibleAssets
          ? strings.contributions.disabledReason.noAssets
          : combinedErrorMessage
            ? strings.contributions.disabledReason.reviewErrors
            : isLoadingData
              ? strings.contributions.disabledReason.loading
              : !hasRecommendations
                ? strings.contributions.disabledReason.noRecommendations
                : ''
    : '';

  useEffect(() => {
    setSaveFeedback(null);
  }, [debouncedAmount]);

  useEffect(() => {
    setAmountInput(currencyInputFormatter.format(amount));
  }, [amount, currencyInputFormatter]);

  const handleAmountChange = (value: string) => {
    const digits = value.replace(/\D/g, '');
    const nextAmount = Number(digits || 0) / 100;
    setAmount(nextAmount);
    setAmountInput(currencyInputFormatter.format(nextAmount));
  };

  const flowSteps = [
    {
      id: 'amount',
      label: strings.contributions.flow.amount,
      status: hasAmount ? 'done' : 'pending',
    },
    {
      id: 'classes',
      label: strings.contributions.flow.classes,
      status: isLoadingStaticData ? 'loading' : hasAssetClasses ? 'done' : 'pending',
    },
    {
      id: 'assets',
      label: strings.contributions.flow.assets,
      status: isLoadingStaticData ? 'loading' : hasEligibleAssets ? 'done' : 'pending',
    },
    {
      id: 'recommendations',
      label: strings.contributions.flow.recommendations,
      status: isLoadingRecommendations ? 'loading' : hasRecommendations ? 'done' : 'pending',
    },
  ] as const;

  const flowStatusCopy = {
    done: strings.contributions.flow.done,
    pending: strings.contributions.flow.pending,
    loading: strings.contributions.flow.loading,
  } as const;
  const nextStep = flowSteps.find((step) => step.status !== 'done');
  const flowTone = nextStep?.status ?? 'done';
  const flowStatusText = !hasAmount
    ? ''
    : nextStep
      ? `${flowStatusCopy[nextStep.status]} - ${nextStep.label}`
      : strings.contributions.flow.ready;
  const showFlowStatus = flowStatusText.length > 0;

  const handleSaveContribution = () => {
    setSaveFeedback(null);
    if (!canSaveContribution || !contributions) {
      setAmountTouched(true);
      setSaveFeedback({ type: 'error', message: strings.contributions.errors.invalid });
      return;
    }

    saveContributionMutation.mutate({ amount, result: contributions });
  };

  return (
    <Layout>
      <section className="contributions app-page">
        <header className="contributions__header">
          <div>
            <p className="contributions__eyebrow">{strings.contributions.header.eyebrow}</p>
            <h1>{strings.contributions.header.title}</h1>
            <span>{strings.contributions.header.subtitle}</span>
          </div>
          <div className="contributions__stats">
            <div className="contributions__stat">
              <span className="contributions__stat-icon contributions__stat-icon--brand">
                <PiggyBank size={18} />
              </span>
              <div>
                <small>{strings.contributions.header.distributed}</small>
                <strong>{currencyFormatter.format(totalInvested)}</strong>
              </div>
            </div>
            <div className={`contributions__stat${hasRemainder ? ' contributions__stat--warning' : ''}`}>
              <span className={`contributions__stat-icon ${hasRemainder ? 'contributions__stat-icon--warning' : 'contributions__stat-icon--info'}`}>
                <Wallet size={18} />
              </span>
              <div>
                <small>{strings.contributions.header.remainder}</small>
                <strong>{currencyFormatter.format(totalCashRemainder)}</strong>
              </div>
            </div>
          </div>
        </header>

        <div className="contributions__panel">
          <div className="contributions__card">
            <label htmlFor="contribution-amount" className="contributions__amount-label">
              {strings.contributions.amount.label}
            </label>
            <div className={`contributions__amount${showAmountError ? ' contributions__amount--invalid' : ''}`}>
              <span className="contributions__currency-symbol">{strings.common.currencySymbol}</span>
              <input
                id="contribution-amount"
                type="text"
                inputMode="numeric"
                value={amountInput}
                onChange={(event) => handleAmountChange(event.target.value)}
                onBlur={() => setAmountTouched(true)}
                aria-invalid={showAmountError}
                aria-describedby={showAmountError ? 'contribution-amount-error' : undefined}
                placeholder="0,00"
              />
            </div>
            {showAmountError ? (
              <span id="contribution-amount-error" className="contributions__field-error" role="alert">
                {strings.contributions.amount.error}
              </span>
            ) : null}
            <p className="contributions__hint">
              {strings.contributions.amount.hint}
            </p>
          </div>
        </div>

        {combinedErrorMessage ? (
          <div className="contributions__feedback contributions__feedback--error" role="alert">
            {combinedErrorMessage}
          </div>
        ) : null}

        <div className="contributions__table-card">
          <div className="contributions__table-head">
            <div>
              <h3>{strings.contributions.table.title}</h3>
              <p>{strings.contributions.table.subtitle}</p>
            </div>
            {showFlowStatus ? (
              <span className={`contributions__status-line contributions__status-line--${flowTone}`}>
                {flowStatusText}
              </span>
            ) : null}
          </div>

          {isLoadingStaticData ? (
            <div className="contributions__empty-state">
              <span className="contributions__empty-icon"><PiggyBank size={28} /></span>
              <strong>Carregando portfólio…</strong>
              <p>Buscando suas classes e ativos configurados.</p>
            </div>
          ) : !hasAmount ? (
            <div className="contributions__empty-state">
              <span className="contributions__empty-icon"><PiggyBank size={28} /></span>
              <strong>Insira o valor do aporte</strong>
              <p>Defina quanto você quer investir agora para ver quais ativos comprar.</p>
            </div>
          ) : isLoadingRecommendations ? (
            <div className="contributions__skeleton-list">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="contributions__skeleton-row">
                  <div className="contributions__skel contributions__skel--ticker" />
                  <div className="contributions__skel contributions__skel--class" />
                  <div className="contributions__skel contributions__skel--badge" />
                  <div className="contributions__skel contributions__skel--price" />
                  <div className="contributions__skel contributions__skel--qty" />
                  <div className="contributions__skel contributions__skel--value" />
                </div>
              ))}
            </div>
          ) : assetClassesCount === 0 ? (
            <div className="contributions__empty-state">
              <span className="contributions__empty-icon"><PiggyBank size={28} /></span>
              <strong>{strings.contributions.states.noClasses}</strong>
            </div>
          ) : eligibleAssetsCount === 0 ? (
            <div className="contributions__empty-state">
              <span className="contributions__empty-icon"><Wallet size={28} /></span>
              <strong>{strings.contributions.states.noAssets}</strong>
            </div>
          ) : !hasRecommendations ? (
            <div className="contributions__empty-state">
              <span className="contributions__empty-icon"><Wallet size={28} /></span>
              <strong>{strings.contributions.states.noSuggestions}</strong>
            </div>
          ) : (
            <div className="contributions__table-wrapper">
              <table className="contributions__table">
                <thead>
                  <tr>
                    <th>{strings.contributions.table.columns.asset}</th>
                    <th>{strings.contributions.table.columns.class}</th>
                    <th className="contributions__th--center">{strings.contributions.table.columns.rating}</th>
                    <th className="contributions__th--right">{strings.contributions.table.columns.price}</th>
                    <th className="contributions__th--right">{strings.contributions.table.columns.quantity}</th>
                    <th className="contributions__th--right">{strings.contributions.table.columns.value}</th>
                  </tr>
                </thead>
                <tbody>
                  {recommendations.map((item) => (
                    <tr key={item.id} className="contributions__tr">
                      <td>
                        <span className="contributions__ticker">{item.ticker}</span>
                      </td>
                      <td>
                        <span
                          className="contributions__class"
                          style={{ '--chip-color': item.classColor } as CSSProperties}
                        >
                          <span className="contributions__class-dot" aria-hidden="true" />
                          {item.className}
                        </span>
                      </td>
                      <td className="contributions__td--center">
                        <span className="contributions__badge">{item.rating}</span>
                      </td>
                      <td className="contributions__td--right">
                        {currencyFormatter.format(item.price)}
                      </td>
                      <td className="contributions__td--right">
                        <strong>{numberFormatter.format(item.quantity)}</strong>
                      </td>
                      <td className="contributions__td--right contributions__td--value">
                        {currencyFormatter.format(item.suggestedValue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="contributions__table-total">
                    <td colSpan={5}>{strings.contributions.table.total}</td>
                    <td className="contributions__td--right">{currencyFormatter.format(totalSuggestedValue)}</td>
                  </tr>
                  {totalCashRemainder > 0 ? (
                    <tr className="contributions__table-total contributions__table-total--remainder">
                      <td colSpan={5}>{strings.contributions.table.remainder}</td>
                      <td className="contributions__td--right">{currencyFormatter.format(totalCashRemainder)}</td>
                    </tr>
                  ) : null}
                </tfoot>
              </table>
            </div>
          )}

          {saveFeedback ? (
            <div
              className={`contributions__feedback ${
                saveFeedback.type === 'error'
                  ? 'contributions__feedback--error'
                  : 'contributions__feedback--success'
              }`}
              role={saveFeedback.type === 'error' ? 'alert' : 'status'}
            >
              {saveFeedback.message}
            </div>
          ) : null}

          {hasAmount && (
            <div className="contributions__save-section">
              <button
                type="button"
                className="contributions__save-button"
                onClick={handleSaveContribution}
                disabled={!canSaveContribution || isSavingContribution}
              >
                <Save size={16} />
                {saveButtonLabel}
              </button>
              {!canSaveContribution && saveDisabledReason ? (
                <span className="contributions__save-hint">{saveDisabledReason}</span>
              ) : null}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
